import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { getBetString } from "../../utils/index";
import { IPick } from "../../interfaces/index";
import { ApiConsumer } from "../../services/ApiConsumer";
import AleaControlShell from "../../communication/AleaControlShell";

interface IPicks {
  pickedStakes: IPick[];
  pickingHistory: string[];
  currentStakeAmount: number;
  shouldShowYourPick: boolean;
  ticketIdentification: string;
}

const initialState: IPicks = {
  pickedStakes: [],
  pickingHistory: [],
  currentStakeAmount: 0,
  shouldShowYourPick: false,
  ticketIdentification: "",
};

export const fetchHistory = createAsyncThunk(
  "fetchHistory/history",
  async () => {
    const response = await ApiConsumer.getHistory();
    return response;
  }
);

export const ticketPayout = createAsyncThunk(
  "api/bet/Payout",
  async (ticketIdentification: string) => {
    const response = await ApiConsumer.createTicketPayout(ticketIdentification);
    return response;
  }
);

export const userPickSlice = createSlice({
  name: "userPick",
  initialState,
  reducers: {
    addAllMultipliers: (state, action) => {
      const { value, stakes, stakesSum } = action.payload;

      const stakeIndex = state.pickedStakes.findIndex(
        (stake) => stake.value === value
      );

      if (stakeIndex < 0) {
        state.pickedStakes.push({
          value,
          valueMultipliers: stakes,
        });
        state.currentStakeAmount += stakesSum;
      } else {
        state.currentStakeAmount +=
          stakesSum -
          state.pickedStakes[stakeIndex].valueMultipliers.reduce(
            (a: number, b: number) => a + b,
            0
          );
        state.pickedStakes[stakeIndex].valueMultipliers = stakes;
      }
    },
    removeAllMultipliers: (state, action) => {
      const value = action.payload;

      const stakeIndex = state.pickedStakes.findIndex(
        (stake) => stake.value === value
      );
      state.currentStakeAmount -= state.pickedStakes[
        stakeIndex
      ].valueMultipliers.reduce((a, b) => a + b, 0);
      state.pickedStakes.splice(stakeIndex, 1);
    },
    addUserPick: (state, action) => {
      const { value, valueMultiplier } = action.payload;

      const stakeIndex = state.pickedStakes.findIndex(
        (stake) => stake.value === value
      );

      if (stakeIndex < 0) {
        state.pickedStakes.push({
          value,
          valueMultipliers: [valueMultiplier],
        });
      } else {
        state.pickedStakes[stakeIndex].valueMultipliers.push(valueMultiplier);
      }

      state.currentStakeAmount += +valueMultiplier;
    },
    removeUserPick: (state, action) => {
      const { value, valueMultiplier } = action.payload;

      const stakeIndex = state.pickedStakes.findIndex(
        (stake) => stake.value === value
      );

      const multiplierIndex = state.pickedStakes[
        stakeIndex
      ].valueMultipliers.indexOf(valueMultiplier);

      state.pickedStakes[stakeIndex].valueMultipliers.splice(
        multiplierIndex,
        1
      );

      if (state.pickedStakes[stakeIndex].valueMultipliers.length === 0) {
        state.pickedStakes.splice(stakeIndex, 1);
      }

      state.currentStakeAmount -= +valueMultiplier;
    },
    setLastPickHistory: (state) => {
      state.pickingHistory = [];
      state.pickingHistory = state.pickedStakes.map(
        (pickedStake) => pickedStake.value
      );
    },
    clearAllUserPicks: (state) => {
      state.pickedStakes.splice(0, state.pickedStakes.length);
      state.currentStakeAmount = 0;
    },
    updatePickingHistory: (state, action) => {
      state.pickingHistory = action.payload;
    },
    setShouldShowYourPick: (state, action) => {
      state.shouldShowYourPick = action.payload;
    },
    setTicketIdentification: (state, { payload }) => {
      state.ticketIdentification = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchHistory.fulfilled, (state, { payload }) => {
      state.pickingHistory = payload.map((item) => getBetString(item));
    });

    builder.addCase(ticketPayout.fulfilled, () => {
      AleaControlShell.refreshBalance();
    });
  },
});

export const {
  addAllMultipliers,
  removeAllMultipliers,
  addUserPick,
  removeUserPick,
  clearAllUserPicks,
  updatePickingHistory,
  setTicketIdentification,
  setLastPickHistory,
  setShouldShowYourPick,
} = userPickSlice.actions;

export default userPickSlice.reducer;
